import React, { useState } from 'react';
import Swal from "sweetalert2";
import { Row, Col } from "reactstrap";
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayErrorToast, displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import { createMoisture, getMoistureDailyCount, getMcNumber } from "../../services/MoistureCorrectionServices";
import { getUserAllowedPlants } from '../../services/PlantServices';
import FloatingControls from '../../components/FloatingControls';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import LoadingOverlay from '../../components/LoadingOverlay';
import { applyCorrectionFactors, MOISTURE_MATERIALS } from './MoistureCorrectionUtils';
import { pickDefaultPlantId } from './designMixDefaults';
import { confirmMoistureSoftLimit, formatMoistureDailyCount } from '../../utils/moistureCorrectionUtils';
import { getCurrentDate, getCurrentTime } from '../../utils/DateUtils';

const EMPTY_MOISTURE = {
  id: null,
  plant_id: "",
  moisture_msand: 0,
  moisture_csand: 0,
  moisture_rsand: 0,
  moisture_12mm: 0,
  moisture_20mm: 0,
  absorption_msand: 0,
  absorption_csand: 0,
  absorption_rsand: 0,
  absorption_12mm: 0,
  absorption_20mm: 0,
  correction_factor_msand: 0,
  correction_factor_csand: 0,
  correction_factor_rsand: 0,
  correction_factor_12mm: 0,
  correction_factor_20mm: 0,
  mc_no: "",
  mc_date: getCurrentDate(),
  mc_time: getCurrentTime(),
  is_active: true,
};

function MoistureCorrection() {
  const [error] = useState({});
  const [isSubmitting] = useState(true);
  const [cookies] = useCookies(['myToken']);
  const [inProgress, setInProgress] = useState(false);
  const [plants, setPlants] = useState([]);
  const [inputs, setInputs] = useState(EMPTY_MOISTURE);
  const [dailyCountMeta, setDailyCountMeta] = useState(null);
  const navigate = useNavigate();

  const refreshMcPreview = (plantId, mcDate, mcTime) => {
    if (!plantId) {
      return;
    }
    getMcNumber(cookies, plantId, mcDate, mcTime)
      .then((meta) => {
        setInputs((values) => ({
          ...values,
          mc_no: meta.mc_no || values.mc_no,
          mc_date: meta.mc_date || values.mc_date,
          mc_time: (meta.mc_time || values.mc_time || '').slice(0, 5),
        }));
      })
      .catch(() => {});
  };

  React.useEffect(() => {
    getUserAllowedPlants(cookies)
      .then((plantList) => {
        const plantOptions = (plantList.plant_list || [])
          .filter((obj) => obj.status)
          .map((plant) => ({ value: plant.id, label: plant.plant_alias || plant.name }));
        setPlants(plantOptions);
        const defaultPlantId = pickDefaultPlantId(plantOptions);
        if (defaultPlantId) {
          setInputs((values) => ({ ...values, plant_id: parseInt(defaultPlantId, 10) }));
          refreshMcPreview(defaultPlantId, getCurrentDate(), getCurrentTime());
        }
      })
      .catch((err) => displayError(err.response?.data ?? err.message, "Failed to load plants"));
  }, [cookies]);

  React.useEffect(() => {
    if (!inputs.plant_id) {
      setDailyCountMeta(null);
      return;
    }
    getMoistureDailyCount(cookies, inputs.plant_id)
      .then(setDailyCountMeta)
      .catch(() => setDailyCountMeta(null));
  }, [cookies, inputs.plant_id]);

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    try {
      if (name === 'plant_id') {
        const plantId = parseInt(value, 10);
        setInputs((values) => ({ ...values, plant_id: plantId || "" }));
        if (plantId) {
          refreshMcPreview(plantId, inputs.mc_date || getCurrentDate(), inputs.mc_time || getCurrentTime());
        }
        return;
      }
      if (name === 'mc_date' || name === 'mc_time') {
        const updated = { ...inputs, [name]: value };
        setInputs(updated);
        if (updated.plant_id) {
          refreshMcPreview(updated.plant_id, updated.mc_date, updated.mc_time);
        }
        return;
      }
      if (name === 'is_active') {
        setInputs((values) => ({ ...values, is_active: value === 'true' }));
        return;
      }
      const updates = { [name]: value };
      if (name.startsWith('moisture_')) {
        const material = name.replace('moisture_', '');
        const absorption = parseFloat(inputs[`absorption_${material}`]) || 0;
        updates[`correction_factor_${material}`] = (absorption - parseFloat(value || 0)).toFixed(2);
      } else if (name.startsWith('absorption_')) {
        const material = name.replace('absorption_', '');
        const moisture = parseFloat(inputs[`moisture_${material}`]) || 0;
        updates[`correction_factor_${material}`] = (parseFloat(value || 0) - moisture).toFixed(2);
      }
      setInputs((values) => ({ ...values, ...updates }));
    } catch (e) {
      displayErrorToast(e);
    }
  };

  const buildPayload = () => {
    const fields = applyCorrectionFactors({
      plant_id: parseInt(inputs.plant_id, 10),
      moisture_msand: parseFloat(inputs.moisture_msand) || 0,
      moisture_csand: parseFloat(inputs.moisture_csand) || 0,
      moisture_rsand: parseFloat(inputs.moisture_rsand) || 0,
      moisture_12mm: parseFloat(inputs.moisture_12mm) || 0,
      moisture_20mm: parseFloat(inputs.moisture_20mm) || 0,
      absorption_msand: parseFloat(inputs.absorption_msand) || 0,
      absorption_csand: parseFloat(inputs.absorption_csand) || 0,
      absorption_rsand: parseFloat(inputs.absorption_rsand) || 0,
      absorption_12mm: parseFloat(inputs.absorption_12mm) || 0,
      absorption_20mm: parseFloat(inputs.absorption_20mm) || 0,
    });
    return {
      ...fields,
      plant: fields.plant_id,
      mc_date: inputs.mc_date,
      mc_time: inputs.mc_time,
      is_active: inputs.is_active !== false,
      correction_factor_msand: parseFloat(fields.correction_factor_msand) || 0,
      correction_factor_csand: parseFloat(fields.correction_factor_csand) || 0,
      correction_factor_rsand: parseFloat(fields.correction_factor_rsand) || 0,
      correction_factor_12mm: parseFloat(fields.correction_factor_12mm) || 0,
      correction_factor_20mm: parseFloat(fields.correction_factor_20mm) || 0,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!inputs.plant_id) {
      Swal.fire('Please select a plant.', '', 'info');
      return;
    }
    if (!((Object.values(error).every((x) => !x)) && isSubmitting)) {
      return;
    }
    const canProceed = await confirmMoistureSoftLimit(cookies, inputs.plant_id, 'save');
    if (!canProceed) {
      return;
    }
    Swal.fire({
      title: 'Do you want to save?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then((result) => {
      if (!result.isConfirmed) {
        Swal.fire('Not saved', '', 'info');
        return;
      }
      setInProgress(true);
      createMoisture(cookies, buildPayload())
        .then((response) => {
          setInProgress(false);
          Swal.fire('Saved!', response?.mc_no ? `MC ${response.mc_no} created.` : '', 'success');
          navigate('/MoistureCorrectionTable');
        })
        .catch((err) => {
          setInProgress(false);
          displayError(err.response?.data ?? err.message, "Saved Failed");
        });
    });
  };

  const Cancel = () => setInputs({
    ...EMPTY_MOISTURE,
    plant_id: inputs.plant_id,
    mc_date: getCurrentDate(),
    mc_time: getCurrentTime(),
  });

  return (
    <>
      <div id="outer-container" className="App">
        <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
        <ErrorToast />
        <LoadingOverlay inProgress={inProgress} />
        <div id="page-wrap">
          <form onSubmit={handleSubmit}>
            <div className="premium-page-header-wrap">
              <PremiumPageHeader
                module="QUALITY CONTROL"
                title="Moisture Correction"
                subtitle="Select a plant, then enter moisture and absorption. New MC is Active by default. CF% = Absorption − Moisture. Deactivate older MC when a new session MC is approved."
                modeLabel="ADD MODE"
              />
              {dailyCountMeta && (
                <p className="text-center mb-0 moisture-correction-count" style={{ color: dailyCountMeta.soft_limit_reached ? '#b45309' : '#1e40af', fontWeight: 600 }}>
                  {formatMoistureDailyCount(dailyCountMeta)}
                  {dailyCountMeta.soft_limit_reached ? ' — additional saves allowed with confirmation' : ''}
                </p>
              )}
            </div>
            <FloatingControls tableLink="/MoistureCorrectionTable" onCancel={Cancel} enableCancel={true} />
            <div className="container-fluid">
              <Row>
                <Col xl={12} lg={12} md={12}>
                  <div className="table-bordered shadow p-3 my-2 border-secondary mb-2 bg-blue form-control-panel moisture-correction-panel">
                    <div className="moisture-correction-meta">
                      <div className="moisture-correction-meta-cell">
                        <label htmlFor="plant_id">Plant</label>
                        <select
                          id="plant_id"
                          name="plant_id"
                          className="form-control mandatory-form-control"
                          required
                          value={inputs.plant_id || ""}
                          onChange={handleChange}
                        >
                          <option value="">Select plant</option>
                          {plants.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="moisture-correction-meta-cell">
                        <label>MC No.</label>
                        <input type="text" className="form-control" value={inputs.mc_no || ''} readOnly style={{ cursor: 'not-allowed' }} />
                      </div>
                      <div className="moisture-correction-meta-cell moisture-correction-meta-cell-datetime">
                        <label htmlFor="mc_date">Date</label>
                        <input type="date" id="mc_date" name="mc_date" className="form-control mandatory-form-control" value={inputs.mc_date || ''} onChange={handleChange} required />
                        <label htmlFor="mc_time">Time</label>
                        <input type="time" id="mc_time" name="mc_time" className="form-control mandatory-form-control" value={inputs.mc_time || ''} onChange={handleChange} required />
                      </div>
                      <div className="moisture-correction-meta-cell">
                        <label htmlFor="is_active">Status</label>
                        <select
                          id="is_active"
                          name="is_active"
                          className="form-control mandatory-form-control"
                          value={inputs.is_active === false ? 'false' : 'true'}
                          onChange={handleChange}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>
                    </div>

                    <div className="moisture-correction-grid">
                      <span />
                      <h5 className="moisture-correction-head"><b>MOISTURE (%)</b></h5>
                      <h5 className="moisture-correction-head"><b>ABSORPTION (%)</b></h5>
                      <h5 className="moisture-correction-head"><b>CORRECTION FACTOR (%)</b></h5>

                      {MOISTURE_MATERIALS.map(([key, label]) => (
                        <React.Fragment key={key}>
                          <label className="moisture-correction-label">{label}</label>
                          <input
                            type="number"
                            step="any"
                            name={`moisture_${key}`}
                            onChange={handleChange}
                            value={inputs[`moisture_${key}`] || 0}
                            max="15"
                            min="0"
                            className="form-control mandatory-form-control"
                            required
                          />
                          <input
                            type="number"
                            step="any"
                            name={`absorption_${key}`}
                            onChange={handleChange}
                            value={inputs[`absorption_${key}`] || 0}
                            max="5"
                            min="0"
                            className="form-control mandatory-form-control"
                            required
                          />
                          <input
                            type="number"
                            name={`correction_factor_${key}`}
                            value={inputs[`correction_factor_${key}`] || 0}
                            className="form-control"
                            readOnly
                            style={{ cursor: "not-allowed" }}
                            required
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default MoistureCorrection;
